import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import type { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { useOrders } from '~hooks/useOrders/useOrders';
import { PhaseBasic, ProjectTypeahead } from '~hooks/useProjects/models';
import useProjects from '~hooks/useProjects/useProjects';

import AddPhasesProjectSelector from './AddPhasesProjectSelector';

// Bounds the single PATCH transaction that writes the copied phases in one request.
const MAX_PHASES_PER_SUBMIT = 100;
const PHASES_PAGE_LIMIT = 30;
const INFINITE_SCROLL_OFFSET_TRIGGER_IN_PX = 100;

type Props = {
  companyId: string;
  currentProjectId?: string;
  existingPhases: PhaseBasic[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
  orderId: string;
};

function getPhasePairKey(phase: Pick<PhaseBasic, 'code' | 'name'>) {
  return JSON.stringify([phase.name, phase.code]);
}

export default function AddPhasesFromProjectDialog({
  companyId,
  currentProjectId,
  existingPhases,
  isOpen,
  onClose,
  onSuccess,
  orderId,
}: Props) {
  const { addNewPhasesToOrder } = useOrders();
  const { getProjectPhasesTypeahead } = useProjects();
  const getProjectPhasesTypeaheadRef = useRef(getProjectPhasesTypeahead);
  getProjectPhasesTypeaheadRef.current = getProjectPhasesTypeahead;

  const requestGenerationRef = useRef(0);
  const isFetchingRef = useRef(false);
  const [selectedProject, setSelectedProject] =
    useState<AutocompleteFieldOption<ProjectTypeahead> | null>(null);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [phases, setPhases] = useState<PhaseBasic[]>([]);
  const [nextCursor, setNextCursor] = useState<string>();
  const [selectedPhaseIds, setSelectedPhaseIds] = useState<Set<string>>(new Set());
  const selectedPhaseIdsRef = useRef<Set<string>>(new Set());
  const [selectedPhaseRecords, setSelectedPhaseRecords] = useState<
    Map<string, PhaseBasic>
  >(new Map());
  const [isFetching, setIsFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const excludedProjectIds = useMemo(
    () => (currentProjectId ? [currentProjectId] : undefined),
    [currentProjectId],
  );
  const existingPhasePairKeys = useMemo(
    () => new Set(existingPhases.map(getPhasePairKey)),
    [existingPhases],
  );

  const resetState = useCallback(() => {
    requestGenerationRef.current += 1;
    setSelectedProject(null);
    const emptySelectedPhaseIds = new Set<string>();
    selectedPhaseIdsRef.current = emptySelectedPhaseIds;
    setSelectedPhaseIds(emptySelectedPhaseIds);
    setSelectedPhaseRecords(new Map());
    setSearchText('');
    setDebouncedSearchText('');
    setPhases([]);
    setNextCursor(undefined);
    setIsFetching(false);
    isFetchingRef.current = false;
    setIsSubmitting(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS);

    return () => window.clearTimeout(timeout);
  }, [searchText]);

  const fetchPhasePage = useCallback(
    async ({
      append,
      cursor,
      projectId,
      query,
    }: {
      append: boolean;
      cursor?: string;
      projectId: string;
      query: string;
    }) => {
      requestGenerationRef.current += 1;
      const requestGeneration = requestGenerationRef.current;
      isFetchingRef.current = true;
      setIsFetching(true);

      try {
        const response = await getProjectPhasesTypeaheadRef.current({
          companyId,
          cursor,
          limit: PHASES_PAGE_LIMIT,
          projectId,
          query: query || undefined,
        });

        if (requestGeneration !== requestGenerationRef.current) return;

        const phasesWithCodes = response?.data.filter((phase) => phase.code) ?? [];
        setPhases((currentPhases) =>
          append ? [...currentPhases, ...phasesWithCodes] : phasesWithCodes,
        );
        setNextCursor(response?.pagination?.after);
      } finally {
        if (requestGeneration === requestGenerationRef.current) {
          isFetchingRef.current = false;
          setIsFetching(false);
        }
      }
    },
    [companyId],
  );

  useEffect(() => {
    if (!isOpen || !selectedProject) return;

    setPhases([]);
    setNextCursor(undefined);
    void fetchPhasePage({
      append: false,
      projectId: selectedProject.value,
      query: debouncedSearchText,
    });
  }, [debouncedSearchText, fetchPhasePage, isOpen, selectedProject]);

  const handleProjectChange = useCallback(
    (project: AutocompleteFieldOption<ProjectTypeahead> | null) => {
      requestGenerationRef.current += 1;
      setSelectedProject(project);
      setSearchText('');
      setDebouncedSearchText('');
      setPhases([]);
      setNextCursor(undefined);
      setIsFetching(false);
      isFetchingRef.current = false;
      const emptySelectedPhaseIds = new Set<string>();
      selectedPhaseIdsRef.current = emptySelectedPhaseIds;
      setSelectedPhaseIds(emptySelectedPhaseIds);
      setSelectedPhaseRecords(new Map());
    },
    [],
  );

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  }, []);

  const handleListScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const list = event.currentTarget;
      const isNearBottom =
        list.scrollTop + list.clientHeight >=
        list.scrollHeight - INFINITE_SCROLL_OFFSET_TRIGGER_IN_PX;

      if (!isNearBottom || !nextCursor || isFetchingRef.current || !selectedProject) {
        return;
      }

      void fetchPhasePage({
        append: true,
        cursor: nextCursor,
        projectId: selectedProject.value,
        query: debouncedSearchText,
      });
    },
    [debouncedSearchText, fetchPhasePage, nextCursor, selectedProject],
  );

  const handlePhaseSelectionChange = useCallback(
    (phase: PhaseBasic, isChecked: boolean) => {
      const currentSelectedPhaseIds = selectedPhaseIdsRef.current;
      const isAlreadySelected = currentSelectedPhaseIds.has(phase.id);
      const selectionLimitReached =
        currentSelectedPhaseIds.size >= MAX_PHASES_PER_SUBMIT && !isAlreadySelected;
      if ((isChecked && selectionLimitReached) || !phase.code) return;

      const nextSelectedPhaseIds = new Set(currentSelectedPhaseIds);
      if (isChecked) {
        nextSelectedPhaseIds.add(phase.id);
      } else {
        nextSelectedPhaseIds.delete(phase.id);
      }
      selectedPhaseIdsRef.current = nextSelectedPhaseIds;
      setSelectedPhaseIds(nextSelectedPhaseIds);
      setSelectedPhaseRecords((currentRecords) => {
        const nextRecords = new Map(currentRecords);
        if (isChecked) {
          nextRecords.set(phase.id, phase);
        } else {
          nextRecords.delete(phase.id);
        }
        return nextRecords;
      });
    },
    [],
  );

  const handleConfirm = useCallback(async () => {
    setIsSubmitting(true);

    try {
      const currentExistingPairKeys = new Set(existingPhases.map(getPhasePairKey));
      const payloadByPair = new Map<string, { code: string; name: string }>();
      for (const phase of selectedPhaseRecords.values()) {
        if (!phase.code) continue;
        const pairKey = getPhasePairKey(phase);
        if (!currentExistingPairKeys.has(pairKey)) {
          payloadByPair.set(pairKey, { code: phase.code, name: phase.name });
        }
      }

      await addNewPhasesToOrder(companyId, orderId, [...payloadByPair.values()]);
      await onSuccess();
      handleClose();
    } catch {
      // Error already surfaced via toaster + Sentry in handleRequestError.
    } finally {
      setIsSubmitting(false);
    }
  }, [
    addNewPhasesToOrder,
    companyId,
    existingPhases,
    handleClose,
    onSuccess,
    orderId,
    selectedPhaseRecords,
  ]);

  return (
    <Dialog
      isOpen={isOpen}
      maxWidth="sm"
      onClose={handleClose}
      preventCloseOnBackdropClick
      preventCloseOnEscapeKeyDown
    >
      <DialogHeader onClose={handleClose}>
        {t('phases.add_from_other_project')}
      </DialogHeader>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <AddPhasesProjectSelector
            companyId={companyId}
            excludeIds={excludedProjectIds}
            label={t('phases.select_project')}
            onChange={handleProjectChange}
            value={selectedProject}
          />

          {!selectedProject ? (
            <Typography
              data-test-id="add-phases-project-prompt"
              sx={{ color: 'text.secondary' }}
            >
              {t('phases.select_project')}
            </Typography>
          ) : (
            <>
              <TextField
                data-test-id="add-phases-search"
                fullWidth
                onChange={handleSearchChange}
                placeholder={t('form_fields.search')}
                size="small"
                value={searchText}
              />
              <Typography data-test-id="add-phases-selected-count" variant="body2">
                {t('phases.selected_count', {
                  count: selectedPhaseIds.size,
                  max: MAX_PHASES_PER_SUBMIT,
                })}
              </Typography>
              <Box
                data-test-id="add-phases-list"
                onScroll={handleListScroll}
                sx={{ maxHeight: 320, overflowY: 'auto' }}
              >
                {phases.length === 0 && !isFetching ? (
                  <Typography sx={{ color: 'text.secondary' }}>
                    {t('phases.no_phases')}
                  </Typography>
                ) : null}
                {phases.map((phase) => {
                  const phaseAlreadyExists = existingPhasePairKeys.has(
                    getPhasePairKey(phase),
                  );
                  const phaseIsSelected = selectedPhaseIds.has(phase.id);
                  const selectionLimitReached =
                    selectedPhaseIds.size >= MAX_PHASES_PER_SUBMIT && !phaseIsSelected;
                  const phaseIsDisabled =
                    phaseAlreadyExists || !phase.code || selectionLimitReached;
                  const checkboxInputProps = {
                    'aria-label': phase.name,
                    'data-test-id': `add-phase-checkbox-${phase.id}`,
                  } as React.InputHTMLAttributes<HTMLInputElement>;

                  return (
                    <Box
                      data-ui="add-phases-phase-row"
                      key={phase.id}
                      sx={{ alignItems: 'center', display: 'flex', gap: 1, py: 0.5 }}
                    >
                      <Checkbox
                        checked={phaseAlreadyExists || phaseIsSelected}
                        disabled={phaseIsDisabled}
                        slotProps={{ input: checkboxInputProps }}
                        onChange={(event) =>
                          handlePhaseSelectionChange(phase, event.target.checked)
                        }
                        size="small"
                      />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2">{phase.name}</Typography>
                        {phase.code ? (
                          <Typography sx={{ color: 'text.secondary' }} variant="caption">
                            {phase.code}
                          </Typography>
                        ) : null}
                      </Box>
                      {phaseAlreadyExists ? (
                        <Typography sx={{ color: 'text.secondary' }} variant="caption">
                          {t('phases.already_exists')}
                        </Typography>
                      ) : null}
                    </Box>
                  );
                })}
                {isFetching ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : null}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <SmallButton disabled={isSubmitting} onClick={handleClose} variant="outlined">
          {t('actions.cancel')}
        </SmallButton>
        <SmallLoadingButton
          data-test-id="add-phases-confirm"
          disabled={isSubmitting || selectedPhaseIds.size === 0}
          loading={isSubmitting}
          onClick={handleConfirm}
          variant="contained"
        >
          {t('phases.add_phase')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}
